/**
 * Thin read layer over Office.js. Attachment bytes and inline-image correlation live in
 * their own module so a Graph-backed implementation could replace them later without
 * touching the rest of the pane.
 */

export interface Recipient {
  name: string;
  email: string;
}

export interface MessageSnapshot {
  /** Subject with any Re:/Fw: prefixes already removed by the host. */
  subject: string;
  sender: Recipient | null;
  to: Recipient[];
  cc: Recipient[];
  bodyHtml: string;
  hasAttachments: boolean;
}

function toRecipient(r: Office.EmailAddressDetails): Recipient {
  return { name: r.displayName ?? '', email: r.emailAddress ?? '' };
}

function getBodyHtml(item: Office.MessageRead): Promise<string> {
  return new Promise((resolve, reject) => {
    item.body.getAsync(Office.CoercionType.Html, (result) => {
      if (result.status === Office.AsyncResultStatus.Succeeded) resolve(result.value ?? '');
      else reject(result.error ?? new Error('Could not read the message body'));
    });
  });
}

export function currentItem(): Office.MessageRead | null {
  const item = Office.context?.mailbox?.item;
  // itemType distinguishes a message from an appointment; the manifest already restricts
  // activation to messages, but a null item is normal when no message is selected.
  if (!item || item.itemType !== Office.MailboxEnums.ItemType.Message) return null;
  return item as Office.MessageRead;
}

export async function readMessage(): Promise<MessageSnapshot> {
  const item = currentItem();
  if (!item) throw new Error('No message is open.');

  // `from` is the authoring mailbox and `sender` the sending one; they differ only for
  // delegate and shared-mailbox sends. The requester should be whoever wrote it.
  const origin = item.from ?? item.sender ?? null;

  return {
    subject: item.normalizedSubject ?? item.subject ?? '',
    sender: origin ? toRecipient(origin) : null,
    to: (item.to ?? []).map(toRecipient),
    cc: (item.cc ?? []).map(toRecipient),
    bodyHtml: await getBodyHtml(item),
    hasAttachments: (item.attachments ?? []).length > 0,
  };
}
